'use client';

import { MarketId } from '@velocity-exchange/common';
import useMarketsInfoStore, {
	MarketsInfoStore,
} from 'src/stores/useMarketsInfoStore';
import {
	AMM_RESERVE_PRECISION,
	BASE_PRECISION,
	PerpMarketAccount,
	PRICE_PRECISION_EXP,
	SpotMarketAccount,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import {
	marketPrecisionFromAccount,
	marketPrecisionFromSizes,
	stepFractionDigits,
} from '@velocity-exchange/common/format';
import { PerpMarketInfo, SpotMarketInfo } from 'src/stores/types';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useSpotMarketsSortedByMarketIndex } from './spotMarkets/useSpotMarketsSortedByMarketIndex';

const DEFAULT_STEP_SIZE = 0.1;

const DEFAULT_SIZE_DIGITS = stepFractionDigits(DEFAULT_STEP_SIZE);

const getStepSizeFromPerpMarketAccount = (account: PerpMarketAccount) => {
	const stepSizeBn = account.orderStepSize;
	return stepSizeBn.toNumber() / AMM_RESERVE_PRECISION.toNumber();
};

const getStepSizeFromSpotMarketAccount = (
	account: SpotMarketAccount,
	spotMarketsSortedByMarketIndex: SpotMarketConfig[]
) => {
	if (!account) return DEFAULT_STEP_SIZE;

	return (
		account.orderStepSize.toNumber() /
		spotMarketsSortedByMarketIndex[account.marketIndex].precision.toNumber()
	);
};

export const getMarketStepSize = (
	marketId: MarketId,
	marketInfoStoreGetter: MarketsInfoStore['get'],
	spotMarketsSortedByMarketIndex: SpotMarketConfig[]
) => {
	const selectedMarketInfo = marketId
		? marketInfoStoreGetter().getMarketInfoByIndexAndType(
				marketId.marketIndex,
				marketId.marketType
			)
		: undefined;

	let stepSize = DEFAULT_STEP_SIZE;

	if (!selectedMarketInfo) {
		return DEFAULT_STEP_SIZE;
	}

	if (marketId.isPerp) {
		stepSize = getStepSizeFromPerpMarketAccount(
			(selectedMarketInfo as PerpMarketInfo).account
		);
	} else {
		stepSize = getStepSizeFromSpotMarketAccount(
			(selectedMarketInfo as SpotMarketInfo).account,
			spotMarketsSortedByMarketIndex
		);
	}

	return stepSize;
};

// Returns the step size of a market. e.g. SOL-PERP has a step size of 0.1
export const useMarketStepSize = (marketId: MarketId | undefined) => {
	const storeGetter = useMarketsInfoStore((s) => s.get);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	if (!marketId) return DEFAULT_STEP_SIZE;
	return getMarketStepSize(
		marketId,
		storeGetter,
		spotMarketsSortedByMarketIndex
	);
};

export const useCurrentMarketStepSize = () => {
	const currentlySelectedMarket = useVelocityStore(
		(s) => s.selectedMarket.current
	);
	return useMarketStepSize(currentlySelectedMarket?.marketId);
};

/**
 * A perp market documents `orderStepSize` at base precision, which is what the
 * SDK adapter assumes; a spot market documents it in the market's own decimals,
 * so that one is built by hand.
 */
const getSizeDigitsFromAccount = (
	marketId: MarketId,
	account: PerpMarketAccount | SpotMarketAccount | undefined,
	spotMarketsSortedByMarketIndex: SpotMarketConfig[]
) => {
	if (!account) return DEFAULT_SIZE_DIGITS;

	if (marketId.isPerp) {
		return marketPrecisionFromAccount(account).sizeDecimals;
	}

	const spotConfig =
		spotMarketsSortedByMarketIndex[(account as SpotMarketAccount).marketIndex];

	if (!spotConfig) return DEFAULT_SIZE_DIGITS;

	return marketPrecisionFromSizes({
		tickSize: account.orderTickSize,
		tickPrecisionExp: PRICE_PRECISION_EXP,
		stepSize: account.orderStepSize,
		stepPrecisionExp: spotConfig.precisionExp,
	}).sizeDecimals;
};

/**
 * Fraction digits a size for this market may carry, read off the on-chain step
 * rather than off the float `getMarketStepSize` divides out.
 */
export const getMarketSizeDigits = (
	marketId: MarketId,
	marketInfoStoreGetter: MarketsInfoStore['get'],
	spotMarketsSortedByMarketIndex: SpotMarketConfig[]
) => {
	const selectedMarketInfo = marketId
		? marketInfoStoreGetter().getMarketInfoByIndexAndType(
				marketId.marketIndex,
				marketId.marketType
			)
		: undefined;

	if (!selectedMarketInfo) return DEFAULT_SIZE_DIGITS;

	return getSizeDigitsFromAccount(
		marketId,
		(selectedMarketInfo as PerpMarketInfo | SpotMarketInfo).account,
		spotMarketsSortedByMarketIndex
	);
};

export const useMarketSizeDigits = (marketId: MarketId | undefined) => {
	const storeGetter = useMarketsInfoStore((s) => s.get);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	if (!marketId) return DEFAULT_SIZE_DIGITS;
	return getMarketSizeDigits(
		marketId,
		storeGetter,
		spotMarketsSortedByMarketIndex
	);
};

export const useCurrentMarketSizeDigits = () => {
	const currentlySelectedMarket = useVelocityStore(
		(s) => s.selectedMarket.current
	);
	return useMarketSizeDigits(currentlySelectedMarket?.marketId);
};

const getMarketStepSizeBn = (
	marketId: MarketId,
	marketInfoStoreGetter: MarketsInfoStore['get']
) => {
	const selectedMarketInfo = marketId
		? marketInfoStoreGetter().getMarketInfoByIndexAndType(
				marketId.marketIndex,
				marketId.marketType
			)
		: undefined;

	if (!selectedMarketInfo) {
		return BASE_PRECISION;
	}

	if (marketId.isPerp) {
		return (selectedMarketInfo as PerpMarketInfo).account?.orderStepSize;
	} else {
		return (selectedMarketInfo as SpotMarketInfo)?.account?.orderStepSize;
	}
};

const useMarketStepSizeBn = (marketId: MarketId | undefined) => {
	const storeGetter = useMarketsInfoStore((s) => s.get);

	if (!marketId) {
		return BASE_PRECISION;
	}

	return getMarketStepSizeBn(marketId, storeGetter);
};

export const useCurrentMarketStepSizeBn = () => {
	const currentlySelectedMarket = useVelocityStore(
		(s) => s.selectedMarket.current
	);

	return useMarketStepSizeBn(currentlySelectedMarket?.marketId);
};
